import { Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useGetTitlesForAccountsMutation } from "../../../../api";
import { AccountsForScraping, JobTitle } from "../../../../types/apiTypes";
import { BaseModal } from "../../BaseModal";
import { CustomButton } from "../../CustomButton";
import { AccordionHeader, ReuseAccordion } from "../../ReuseAccordion";
import { AccountsList } from "./AccountsList";
import { EditJobTitlesModal } from "./EditJobTitlesModal";
import { useAppDispatch } from "../../../../hooks";
import { setIsNotificationsOpen } from "../../../../features/companySlice";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  campaign_id: string;
  accountsForScraping: AccountsForScraping;
};

export const GetMoreContactsForAccountsModal = ({
  isOpen,
  setIsOpen,
  campaign_id,
  accountsForScraping,
}: Props) => {
  const dispatch = useAppDispatch();
  const [titles, setTitles] = useState<JobTitle[]>([]);
  const [isContactsModalOpen, setIsContactsModalOpen] =
    useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const [selectedPrefiltered, setSelectedPrefiltered] = useState<string[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  const [getTitlesForAccounts, { isLoading }] =
    useGetTitlesForAccountsMutation();

  useEffect(() => {
    const prefilteredIds = accountsForScraping.prefiltered.map(({ id }) => id);
    const availableIds = accountsForScraping.all.map(({ id }) => id);

    setSelectedPrefiltered((prev) => {
      if (prev.length === 0) {
        return prefilteredIds;
      }

      return prev.filter((id) => prefilteredIds.includes(id));
    });

    setSelectedAvailable((prev) =>
      prev.filter((id) => availableIds.includes(id)),
    );
  }, [accountsForScraping]);

  useEffect(() => {
    setIsVisible(isContactsModalOpen);
  }, [isContactsModalOpen]);

  const submitHandler = () => {
    const account_ids = [...selectedPrefiltered, ...selectedAvailable];

    getTitlesForAccounts({
      campaign_id,
      account_ids,
    })
      .unwrap()
      .then((data) => {
        setSelectedAccounts(account_ids);
        setTitles(
          data.title_gaps.map((el) => ({
            is_critical: el.is_critical,
            title: el.name,
            id: el.name,
            is_edit: false,
          })),
        );
        setIsContactsModalOpen(true);
      });
  };

  const cancelHandler = () => {
    setIsOpen(false);
    dispatch(setIsNotificationsOpen(true));
  };

  return (
    <BaseModal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title="Explore INFUSE Audience Expansion"
      sx={{
        padding: "32px",
        maxWidth: "548px",
        width: "94%",
        visibility: isVisible ? "hidden" : "visible",
      }}
      btnBlock={
        <Stack direction="row" gap="8px" paddingTop="16px">
          <CustomButton
            onClick={() => setIsOpen(false)}
            variant="contained"
            textColor="#4C545B"
            fullWidth
            type="button"
            customColor="#F1F1F1"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={submitHandler}
            variant="contained"
            fullWidth
            type="button"
            customColor="#1C3C6C"
            loading={isLoading}
            disabled={
              isLoading ||
              (selectedAvailable.length === 0 &&
                selectedPrefiltered.length === 0)
            }
          >
            Next Step
          </CustomButton>
        </Stack>
      }
    >
      <Stack gap="16px">
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1" color="text.secondary">
            Edit information about the product
          </Typography>
        </Stack>

        <Stack gap="16px">
          {accountsForScraping.prefiltered.length > 0 && (
            <ReuseAccordion
              header={(expanded) => (
                <AccordionHeader expanded={expanded} title="Prefiltered" />
              )}
            >
              <AccountsList
                accounts={accountsForScraping.prefiltered}
                selectedIds={selectedPrefiltered}
                setSelectedIds={setSelectedPrefiltered}
                withSelectAll
              />
            </ReuseAccordion>
          )}

          {accountsForScraping.all.length > 0 && (
            <ReuseAccordion
              initialExpanded={accountsForScraping.prefiltered.length === 0}
              header={(expanded) => (
                <AccordionHeader
                  expanded={expanded}
                  title="Available Accounts"
                />
              )}
            >
              <AccountsList
                accounts={accountsForScraping.all}
                selectedIds={selectedAvailable}
                setSelectedIds={setSelectedAvailable}
                withSearch
                withSelectAll
              />
            </ReuseAccordion>
          )}
        </Stack>

        {isContactsModalOpen && (
          <EditJobTitlesModal
            selectedAccounts={selectedAccounts}
            onSuccess={cancelHandler}
            campaign_id={campaign_id}
            initTitles={titles}
            isOpen={isContactsModalOpen}
            setIsOpen={setIsContactsModalOpen}
          />
        )}
      </Stack>
    </BaseModal>
  );
};
